/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState } from "react";
import axios from "axios";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalBody,
} from "@chakra-ui/modal";
import Card from "components/card";
import { Button } from '@chakra-ui/react';
import DisplayAmountWithDecimals from "./DisplayAmountWithDecimals";
import { TokenManager } from "utils/tokenManager";

const TradeCancelConfirmModal: React.FC<{
    dataToProcess: any;
    isOpen: boolean;
    onClose(): void;
    triggerReload(): void;
    setSwalProps: any;
}> = ({ isOpen, onClose, triggerReload, dataToProcess, setSwalProps }) => {

    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [isConfirmed, setIsConfirmed] = useState<boolean>(false);
    const userRole = localStorage.getItem("userRole");

    const closeModal = async () => {
        setIsConfirmed(false); // Reset checkbox when closing
        onClose();
    };

    const handleSubmit = async () => {
        try {
            setIsProcessing(true);

            const data = {
                username: localStorage.getItem("username"),
                userRole: userRole,
                id: dataToProcess.id,
                shortid: dataToProcess.shortid,
                subaccountName: dataToProcess.subaccount.name,
                status: "cancelled",
            }

            await axios.post(process.env.REACT_APP_API_URL + `/api/${userRole.toLowerCase()}/trade/action/update-trade-status`, data)
                .then(function (response) {
                    if (response && response.data?.status === 'ok') {
                        closeModal();
                        setSwalProps({
                            show: true,
                            icon: "success",
                            title: "Success",
                            html: "Trade has been cancelled",
                            showConfirmButton: true,
                            didClose: () => {
                                triggerReload();
                                setSwalProps({});
                                setIsProcessing(false);
                            },
                        });
                    } else {
                        setIsProcessing(false);
                    }
                })
                .catch(function (error) {
                    setIsProcessing(false);
                    setSwalProps({
                        show: true,
                        icon: "error",
                        title: "Oops!",
                        html: error.response.data.message,
                        showConfirmButton: true,
                        didClose: () => {
                            setSwalProps({});
                            setIsProcessing(false);
                        },
                    });
                });
        } catch (error: any) {
            setIsProcessing(false);
            setSwalProps({
                show: true,
                icon: "error",
                title: "Oops!",
                html: "Something went wrong, please try again later.",
                showConfirmButton: true,
                didClose: () => {
                    setSwalProps({});
                    setIsProcessing(false);
                },
            });
        }
    };

    const formatDate = (timestamp: string) => {
        if (!timestamp) return "-";
        const date = new Date(parseInt(timestamp));
        return date.toLocaleString();
    };

    const formatStatus = (status: string) => {
        return status.charAt(0).toUpperCase() + status.slice(1);
    };

    const formatAddress = (address?: string): string => {
        if (!address || address.length < 24) return 'N/A';
        return `${address.slice(0, 15)}...${address.slice(-9)}`;
    };

    return (
        <>
            {dataToProcess &&
                <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
                    <ModalOverlay className="bg-[#000] !opacity-30" />
                    <ModalContent className="!z-[1002] !m-auto !w-max min-w-[350px] !max-w-[85%] md:top-[12vh]">
                        <ModalBody className="!text-navy-700">
                            <Card extra="px-[30px] pt-[35px] pb-[40px] max-w-[650px] flex flex-col !z-[1004]">
                                <h1 className="mb-[20px] text-2xl font-bold">Cancel Trade Confirmation</h1>
                                <div className="mb-[20px] text-sm">
                                    <div className="mb-8">
                                        You're about to cancel this trade. This action cannot be undone.
                                    </div>
                                    <div className="mb-1">
                                        <span>Code:</span>
                                        <span className="ml-3 font-semibold">{dataToProcess.shortid}</span>
                                    </div>
                                    {userRole === "superadmin" &&
                                        <div className="mb-1">
                                            <span>Subaccount:</span>
                                            <span className="ml-3 font-semibold">{dataToProcess.subaccount.name}</span>
                                        </div>
                                    }
                                    <div className="mb-1">
                                        <span>Pair:</span>
                                        <span className="ml-3 font-semibold">{`${dataToProcess.from_asset}/${dataToProcess.to_asset} (${dataToProcess.to_network})`}</span>
                                    </div>
                                    <div className="mb-1">
                                        <span>Amount:</span>
                                        <span className="ml-3 font-semibold">
                                            {dataToProcess.from_network === "fiat" ? (
                                                <DisplayAmountWithDecimals
                                                    amount={Number(dataToProcess.from_amount)}
                                                    currency={dataToProcess.from_asset}
                                                    decimal={dataToProcess?.from_asset_decimal}
                                                />
                                            ) : (
                                                dataToProcess.from_amount
                                            )}
                                            &nbsp;{dataToProcess.from_asset}
                                        </span>
                                    </div>
                                    <div className="mb-1">
                                        <span>To Address:</span>
                                        <span className="ml-3 font-semibold">
                                            {`${dataToProcess.to_address.nickname} (${formatAddress(dataToProcess.to_address.address)})`}
                                        </span>
                                    </div>
                                    <div className="mb-1">
                                        <span>Ref.</span>
                                        <span className="ml-3 font-semibold">
                                            {dataToProcess.reference}
                                        </span>
                                    </div>
                                </div>

                                {/* Confirmation Checkbox */}
                                <div className="flex items-start gap-3 rounded-lg border border-gray-200 dark:border-white/10 p-4 mb-4">
                                    <input
                                        type="checkbox"
                                        id="confirmCancel"
                                        checked={isConfirmed}
                                        onChange={(e) => setIsConfirmed(e.target.checked)}
                                        disabled={isProcessing}
                                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                    />
                                    <label htmlFor="confirmCancel" className="text-sm text-navy-700 dark:text-white cursor-pointer">
                                        I understand that cancelling this trade cannot be undone and I want to proceed with the cancellation.
                                    </label>
                                </div>

                                <div className="flex gap-2 justify-end">
                                    <Button
                                        onClick={() => closeModal()}
                                        isDisabled={isProcessing}
                                        className="btn btn-default"
                                    >
                                        Not now
                                    </Button>
                                    <Button
                                        onClick={() => handleSubmit()}
                                        isLoading={isProcessing}
                                        loadingText='Processing...'
                                        isDisabled={!isConfirmed || isProcessing || TokenManager.isImpersonating()}
                                        className="btn text-white bg-red-600 hover:bg-red-500 disabled:bg-gray-400 disabled:cursor-not-allowed">
                                        Cancel trade
                                    </Button>
                                </div>
                            </Card>
                        </ModalBody>
                    </ModalContent>
                </Modal>
            }
        </>
    );
};

export default TradeCancelConfirmModal;